import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Copy,
  Folder,
  KeyRound,
  Plus,
  RotateCw,
  Trash2,
} from "lucide-react";
import { z } from "zod";
import {
  SftpFolderSchema,
  SftpPasswordSchema,
  SftpUsernameSchema,
  type CreateSftpAccountDto,
  type SftpAccountRecord,
  type SftpAccountWithSecret,
} from "@lumika/shared";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
  useZodForm,
} from "@lumika/ui";
import { sftpApi } from "../api/sftp";
import { ApiError } from "../api/client";

const STATUS_VARIANT: Record<
  SftpAccountRecord["status"],
  "success" | "secondary" | "warning"
> = {
  active: "success",
  disabled: "secondary",
  rotating: "warning",
};

/**
 * Top-level SFTP-credentials admin page.
 *
 * Lists every credential across every dealership. A third-party
 * integrator's account isn't conceptually owned by a single
 * dealership — the column is informational, and admins create one
 * here by picking the dealership the credential is provisioned for.
 *
 * The per-dealership scoped view at `/dealerships/:id/sftp` is
 * retained as a convenience drill-down (it sets the dealership
 * filter on the same data).
 */
// Local form schema composed from the shared SFTP field primitives.
// The dialog can't bind `CreateSftpAccountSchema` directly: optional
// text inputs hold "" while typing (so a partial value mustn't trip
// the regex), and `password` is only meaningful in "type" mode — in
// "generate" mode it stays "" and the server auto-generates. The
// non-empty cases still reuse the exact shared primitives so the
// client and server agree on what's valid.
const CreateSftpFormSchema = z
  .object({
    username: SftpUsernameSchema,
    folder: z.union([z.literal(""), SftpFolderSchema]),
    passwordMode: z.enum(["generate", "type"]),
    password: z.union([z.literal(""), SftpPasswordSchema]),
    notes: z.string().max(2000),
  })
  .superRefine((value, ctx) => {
    if (value.passwordMode === "type" && value.password === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["password"],
        message: "Password must be at least 16 characters.",
      });
    }
  });

type CreateFormState = z.infer<typeof CreateSftpFormSchema>;

const EMPTY_CREATE_FORM: CreateFormState = {
  username: "",
  folder: "",
  passwordMode: "generate",
  password: "",
  notes: "",
};

// nestjs-zod 400 bodies carry `{ errors: ZodIssue[] }` with a flat
// `path` that matches the dialog's field names; a 409 conflict names
// a duplicate username. Pin either back onto the offending field so it
// shows inline instead of only as a toast.
function mapServerErrorToFields(
  error: unknown,
): Partial<Record<keyof CreateFormState, string>> | null {
  if (!(error instanceof ApiError)) return null;

  const body = error.details;
  const issues =
    body && typeof body === "object" && Array.isArray((body as { errors?: unknown }).errors)
      ? (body as { errors: Array<{ path?: unknown; message?: unknown }> }).errors
      : null;
  if (error.status === 400 && issues) {
    const out: Partial<Record<keyof CreateFormState, string>> = {};
    for (const issue of issues) {
      const head = Array.isArray(issue.path) ? issue.path[0] : undefined;
      if (
        typeof head === "string" &&
        head in EMPTY_CREATE_FORM &&
        !(head in out) &&
        typeof issue.message === "string"
      ) {
        out[head as keyof CreateFormState] = issue.message;
      }
    }
    return Object.keys(out).length > 0 ? out : null;
  }

  if (error.status === 409 && /username/i.test(error.message)) {
    return { username: error.message };
  }
  return null;
}

export function SftpAccountsPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const form = useZodForm(CreateSftpFormSchema, EMPTY_CREATE_FORM);
  const [secret, setSecret] = useState<SftpAccountWithSecret | null>(null);

  const accountsQuery = useQuery({
    queryKey: ["admin", "sftp-accounts", "all"],
    queryFn: () => sftpApi.listAccounts(),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateSftpAccountDto) => sftpApi.createAccount(input),
    onSuccess: (created) => {
      setSecret(created);
      setCreateOpen(false);
      form.reset();
      queryClient.invalidateQueries({
        queryKey: ["admin", "sftp-accounts"],
      });
      toast.success(`SFTP user ${created.username} created.`);
    },
    onError: (err: Error) => {
      const fieldErrors = mapServerErrorToFields(err);
      if (fieldErrors) form.setServerErrors(fieldErrors);
      toast.error(err.message);
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) => sftpApi.rotateAccount(id),
    onSuccess: (rotated) => {
      setSecret(rotated);
      queryClient.invalidateQueries({
        queryKey: ["admin", "sftp-accounts"],
      });
      toast.success(`Password rotated for ${rotated.username}.`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => sftpApi.removeAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "sftp-accounts"],
      });
      toast.success("SFTP user deleted.");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (accountsQuery.isLoading) return <PageLoading message="Loading SFTP users…" />;
  if (accountsQuery.error)
    return <PageError description={(accountsQuery.error as Error).message} />;

  const accounts = accountsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="SFTP"
        description="Credentials issued to third-party integrators who push inventory CSVs (and future ADF leads) into Lumika's SFTP endpoint. Browse the underlying storage in the Files view."
        actions={
          <div className="flex gap-2">
            <Button asChild variant="ghost">
              <Link to="/sftp/browser">
                <Folder className="mr-1.5 size-4" /> Browse files
              </Link>
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1.5 size-4" /> New SFTP user
            </Button>
          </div>
        }
      />

      <Section>
        {accounts.length === 0 ? (
          <EmptyState
            icon={<KeyRound className="size-10" />}
            title="No SFTP users yet"
            description="Create one to issue credentials a third-party integrator can use with their SFTP client or scheduled upload script."
          />
        ) : (
          <>
            {/* Desktop: full credentials table. */}
            <TableBleed className="-my-6 hidden overflow-hidden rounded-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Folder</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last seen</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-sm">{a.username}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {a.folder}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[a.status]}>{a.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {a.lastSeenAt ? new Date(a.lastSeenAt).toLocaleString() : "never"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button asChild size="sm" variant="ghost">
                            <Link
                              to={`/sftp/browser?path=${encodeURIComponent(`${a.folder}/`)}`}
                            >
                              <Folder className="mr-1 size-4" /> Files
                            </Link>
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (
                                confirm(
                                  `Rotate password for ${a.username}? The current password stops working immediately.`,
                                )
                              ) {
                                rotateMutation.mutate(a.id);
                              }
                            }}
                            disabled={rotateMutation.isPending}
                          >
                            <RotateCw className="mr-1 size-4" /> Rotate
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label="Delete SFTP user"
                            onClick={() => {
                              if (
                                confirm(
                                  `Delete SFTP user ${a.username}? This cannot be undone.`,
                                )
                              ) {
                                deleteMutation.mutate(a.id);
                              }
                            }}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            {/* Mobile: condensed touch list with the same actions. */}
            <MobileList>
              {accounts.map((a) => (
                <MobileListRow
                  key={a.id}
                  primary={<span className="font-mono">{a.username}</span>}
                  secondary={<span className="font-mono">{a.folder}</span>}
                  tertiary={
                    a.lastSeenAt
                      ? `Last seen ${new Date(a.lastSeenAt).toLocaleString()}`
                      : "Never seen"
                  }
                  meta={
                    <>
                      <Badge variant={STATUS_VARIANT[a.status]}>{a.status}</Badge>
                      <Button asChild size="sm" variant="ghost">
                        <Link
                          to={`/sftp/browser?path=${encodeURIComponent(`${a.folder}/`)}`}
                        >
                          <Folder className="mr-1 size-4" /> Files
                        </Link>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (
                            confirm(
                              `Rotate password for ${a.username}? The current password stops working immediately.`,
                            )
                          ) {
                            rotateMutation.mutate(a.id);
                          }
                        }}
                        disabled={rotateMutation.isPending}
                      >
                        <RotateCw className="mr-1 size-4" /> Rotate
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (
                            confirm(
                              `Delete SFTP user ${a.username}? This cannot be undone.`,
                            )
                          ) {
                            deleteMutation.mutate(a.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="mr-1 size-4 text-destructive" /> Delete
                      </Button>
                    </>
                  }
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) form.reset();
        }}
      >
        <DialogContent
          preventDismiss={
            JSON.stringify(form.values) !== JSON.stringify(EMPTY_CREATE_FORM)
          }
        >
          <DialogHeader>
            <DialogTitle>New SFTP user</DialogTitle>
            <DialogDescription>
              Issue an integrator-facing username + password scoped to a
              reserved folder on the SFTP bucket. The credential is NOT
              tied to a dealership — one integrator can serve many
              (file-to-dealership mapping happens through Inventory
              Imports). Multiple credentials can share a folder;
              usernames must be unique.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <FormField
              label="Username"
              required
              error={form.errors.username}
              description="3–32 chars, lowercase a–z, 0–9, '-', '_'. This is the SFTP login the integrator will type."
            >
              <Input
                value={form.values.username}
                onChange={(e) => form.setField("username", e.target.value)}
                placeholder="e.g. acme-bmw-vauto"
                autoComplete="off"
                className="font-mono"
              />
            </FormField>

            <FormField
              label="Reserved folder"
              optional
              error={form.errors.folder}
              description="Folder on the SFTP bucket the integrator can read/write under. Lowercase a–z, 0–9, '-', '_', '/'. Multiple credentials may share a folder. Blank ⇒ use the username as the folder name."
            >
              <Input
                value={form.values.folder}
                onChange={(e) => form.setField("folder", e.target.value)}
                placeholder="e.g. vauto or homenet/nightly"
                autoComplete="off"
                className="font-mono"
              />
            </FormField>

            <FormField label="Password" error={form.errors.password}>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={form.values.passwordMode === "generate" ? "default" : "outline"}
                    onClick={() => {
                      form.patch({ passwordMode: "generate", password: "" });
                      form.clearError("password");
                    }}
                  >
                    Generate
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={form.values.passwordMode === "type" ? "default" : "outline"}
                    onClick={() => form.setField("passwordMode", "type")}
                  >
                    Type my own
                  </Button>
                </div>
                {form.values.passwordMode === "generate" ? (
                  <p className="text-xs text-muted-foreground">
                    A strong random password is generated server-side and
                    shown to you once below.
                  </p>
                ) : (
                  <Input
                    type="password"
                    value={form.values.password}
                    onChange={(e) => form.setField("password", e.target.value)}
                    placeholder="At least 16 printable ASCII characters"
                    autoComplete="new-password"
                    className="font-mono"
                  />
                )}
              </div>
            </FormField>

            <FormField
              label="Notes"
              optional
              error={form.errors.notes}
              description="Optional — e.g. 'Nightly inventory export from HomeNet'"
            >
              <Textarea
                value={form.values.notes}
                onChange={(e) => form.setField("notes", e.target.value)}
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setCreateOpen(false);
                form.reset();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const result = form.validate();
                if (!result.ok) return;
                const { username, folder, passwordMode, password, notes } = result.data;
                createMutation.mutate({
                  username,
                  folder: folder || undefined,
                  password: passwordMode === "type" && password ? password : undefined,
                  notes: notes.trim() || null,
                });
              }}
              disabled={createMutation.isPending}
            >
              Create user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SecretReveal secret={secret} onClose={() => setSecret(null)} />
    </div>
  );
}

function SecretReveal({
  secret,
  onClose,
}: {
  secret: SftpAccountWithSecret | null;
  onClose: () => void;
}) {
  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard.`);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  };
  return (
    <Dialog open={Boolean(secret)} onOpenChange={(open) => !open && onClose()}>
      {/* The password is shown only once — block accidental outside-click
          and Escape dismissal; Done and the X are the deliberate exits. */}
      <DialogContent preventDismiss>
        {secret && (
          <>
            <DialogHeader>
              <DialogTitle>Credentials ready</DialogTitle>
              <DialogDescription className="text-warning">
                The password is shown only once. Copy it now — Lumika stores
                only a hash and cannot recover it.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 font-mono text-sm">
              <Row label="Host" value={`${secret.host}:${secret.port}`} onCopy={copy} />
              <Row label="Username" value={secret.username} onCopy={copy} />
              <Row label="Password" value={secret.password} onCopy={copy} />
              <Row label="Folder" value={secret.folder} onCopy={copy} />
            </div>
            <DialogFooter>
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (label: string, value: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <Input readOnly value={value} className="flex-1" />
      <Button
        size="icon"
        variant="ghost"
        aria-label={`Copy ${label}`}
        onClick={() => onCopy(label, value)}
      >
        <Copy className="size-4" />
      </Button>
    </div>
  );
}
